// Face sign-in: is THIS browser a face sign-in station? (jobq-api "face sign-in", 2026-10-15)
//
// A station is a browser an Admin registered on the Face sign-in page (/face-id). Registration
// hands back a long random key, kept in this browser's localStorage and sent as X-Face-Station
// on every /face call (src/face/faceApi.js). No key = this computer never opens the camera.
//
// getStationStatus() asks GET /face/station at most once per 5 minutes (in memory, per page
// load). `live: false` (the API's global switch off, or no answer) keeps the camera shut; a
// network error is not cached, so the next PIN prompt asks again. A 401 means the key is gone
// on the API side (revoked, unknown): the stored key is cleared, so this computer stops trying.

import { faceFetch } from './faceFetch';

export const STATION_KEY = 'jobqFaceStationKey';
export const STATION_NAME = 'jobqFaceStationName';
export const STATUS_CACHE_MS = 5 * 60 * 1000;

let cache = null; // { key, at, status }
let inflight = null; // { key, promise }

function storage() {
  try {
    return window.localStorage;
  } catch (e) {
    return null;
  }
}

/** The stored station key, or null (no key, or storage blocked). */
export function getStationKey() {
  try {
    const ls = storage();
    const v = ls ? ls.getItem(STATION_KEY) : null;
    return v && v.trim() ? v.trim() : null;
  } catch (e) {
    return null;
  }
}

/** The station's name as stored here when it was set up (or last read from the API). */
export function getStationName() {
  try {
    const ls = storage();
    return (ls && ls.getItem(STATION_NAME)) || null;
  } catch (e) {
    return null;
  }
}

/** Make this browser a station. Returns false when storage refused it (private window). */
export function setStation(key, name) {
  cache = null;
  inflight = null;
  try {
    const ls = storage();
    if (!ls) return false;
    ls.setItem(STATION_KEY, String(key));
    if (name) ls.setItem(STATION_NAME, String(name));
    else ls.removeItem(STATION_NAME);
    return getStationKey() === String(key).trim();
  } catch (e) {
    return false;
  }
}

/** This browser stops being a station (the API side is untouched: an Admin revokes it there). */
export function clearStation() {
  cache = null;
  inflight = null;
  try {
    const ls = storage();
    if (!ls) return;
    ls.removeItem(STATION_KEY);
    ls.removeItem(STATION_NAME);
  } catch (e) { /* blocked storage: nothing was stored */ }
}

/** Forget the cached GET /face/station answer (the next call asks the API). */
export function invalidateStationStatus() {
  cache = null;
}

/**
 * GET /face/station, cached ~5 min. Resolves (never rejects):
 *   { station: false, live: false }                          no key here (or the API revoked it: `revoked`)
 *   { station: true, live, name, station_id, enrolled_count } the API's answer
 *   { station: true, live: false, error }                    no answer (network / 5xx); not cached
 */
export function getStationStatus({ force = false } = {}) {
  const key = getStationKey();
  if (!key) return Promise.resolve({ station: false, live: false });
  if (!force && cache && cache.key === key && Date.now() - cache.at < STATUS_CACHE_MS) {
    return Promise.resolve(cache.status);
  }
  if (inflight && inflight.key === key) return inflight.promise;
  const promise = faceFetch('/face/station', { headers: { 'X-Face-Station': key } })
    .then((r) => {
      if (getStationKey() !== key) return { station: Boolean(getStationKey()), live: false };
      if (r.ok && r.body && typeof r.body === 'object') {
        const status = {
          station: true,
          live: Boolean(r.body.live),
          name: r.body.name || getStationName(),
          station_id: r.body.station_id != null ? r.body.station_id : null,
          enrolled_count: r.body.enrolled_count != null ? r.body.enrolled_count : null,
        };
        if (r.body.name && r.body.name !== getStationName()) {
          try { const ls = storage(); if (ls) ls.setItem(STATION_NAME, r.body.name); } catch (e) { /* ignore */ }
        }
        cache = { key, at: Date.now(), status };
        return status;
      }
      // Only the API's own "not a face sign-in station" 401 forgets the key. Any other 401 (an API-key
      // hiccup during a deploy) must not unregister all three stations at once.
      if (r.status === 401 && /station/i.test(r.message || '')) {
        clearStation();
        return { station: false, live: false, revoked: true, message: r.message };
      }
      return { station: true, live: false, error: r.message || 'No answer from JobQ.' };
    })
    .catch(() => ({ station: true, live: false, error: 'No answer from JobQ.' }))
    .finally(() => {
      if (inflight && inflight.promise === promise) inflight = null;
    });
  inflight = { key, promise };
  return promise;
}

/** Tests only: forget the cache between tests. */
export function resetFaceStationCacheForTests() {
  cache = null;
  inflight = null;
}
